import { Navigate, Routes, Route } from 'react-router-dom'
import OfflineNavigationGuard from './components/offline-navigation-guard'
import OfflineSessionBootstrap from './components/offline-session-bootstrap'
import {
  createTheme,
  CssBaseline,
  ThemeProvider,
} from '@mui/material'
import { RoutesProvider } from './store/routesContext'
import { DataMergingAndValidation } from './pages/DataMerginAndValidation'
import { MergeContext } from './store/mergeContext'
import Login from './pages/Login'
import Home from './pages/Home'
import Signup from './pages/Signup'
import User from './pages/User'
import Admin from './pages/Admin'
import Statistics from './pages/Statistics'
import History from './pages/History'
import Help from './pages/Help'
import { cloneElement, createContext, useEffect, useMemo, useState } from 'react'
import { LayoutModeProvider, useLayoutMode } from './contexts/layout-mode-context'
import { migrateLegacyPendingToDexie } from './offline/migrate-legacy'
import { useLingui } from '@lingui/react'
import ForgotPwd from './pages/ForgotPwd'
import DeviceProvision from './pages/DeviceProvision'
import PackageImportsList from './pages/PackageImportsList'
import { DesktopSidebarProvider } from './contexts/desktop-sidebar-context'
import { OfflineNavActionsProvider } from './contexts/offline-nav-actions-context'
import { MobileLogoutProvider } from './contexts/mobile-logout-context'
import { ActionLogsProvider } from './store/actionLogsContext'
import ReportAccidentNew from './pages/ReportAccidentNew'
import { DEFAULT_LOCALE } from './consts/default-locale'
import { getMarketDocumentTitle } from './config/market'
import SwUpdateModal from './components/pwa/sw-update-modal'
import { DESKTOP_FORM_DRAWER_WIDTH_PX } from './consts/form-drawer-width'

export const ThemeContext = createContext()

function AppRoutes({
  mode,
  setMode,
  routes,
  handledCrash,
  handleRowClick,
  resetHandledCrash,
}) {
  const { isMobile } = useLayoutMode()

  const toggleTheme = (m) => {
    if (m) {
      setMode(m)
    } else {
      setMode(mode === 'dark' ? 'light' : 'dark')
    }
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <RoutesProvider routes={routes.map((route) => route.path)}>
        <OfflineNavActionsProvider>
          <MobileLogoutProvider>
            <OfflineNavigationGuard />
            <OfflineSessionBootstrap />
            <Routes>
            <Route
              path="/"
              element={<Login setThemeMode={setMode} isMobile={isMobile} />}
            />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Signup isMobile={isMobile} />} />
            <Route
              path="/forgot-pwd"
              element={<ForgotPwd isMobile={isMobile} />}
            />
            <Route
              path="report-accident"
              element={<ReportAccidentNew isMobile={isMobile} />}
            />
            <Route path="/user" element={<User isMobile={isMobile} />} />
            {routes.map(({ path, element }, position) => (
              <Route
                key={path}
                path={path}
                element={cloneElement(element, {
                  position,
                  isMobile,
                  handleRowClick,
                  handledCrash,
                  resetHandledCrash,
                })}
              />
            ))}
          </Routes>
          </MobileLogoutProvider>
        </OfflineNavActionsProvider>
      </RoutesProvider>
    </ThemeContext.Provider>
  )
}

export default function App() {
  const user = JSON.parse(localStorage.getItem('adams_user'))
  const [mode, setMode] = useState(user ? user.theme : 'light')
  const { i18n } = useLingui()
  const locale = i18n.locale || DEFAULT_LOCALE

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
  }, [locale])

  useEffect(() => {
    document.title = getMarketDocumentTitle()
  }, [])

  useEffect(() => {
    migrateLegacyPendingToDexie().catch(() => {})
  }, [])

  const theme = useMemo(
    () =>
      createTheme({
        direction: locale === 'ar' ? 'rtl' : 'ltr',
        palette: {
          mode
          , primary: {
            main: mode === 'light' ? '#10171f' : '#f6d552'
          }
          , background: {
            default: mode === 'light' ? '#fff' : '#10171f'
            , paper: mode === 'light' ? '#fff' : '#10171f'
          },
          newColors: {
            cyan: '#28BDE0',
            yellow: '#E0D028',
            pink: '#E0286C',
            bordeaux: '#8B4860',
            gray: '#425B61',
            lightGray: '#EDEDED',
            black: '#3C3C3B',
            pinkGradient: 'linear-gradient(90deg, #941B47, #E0296B)',
            cyanGradient: 'linear-gradient(90deg, #007B98, #28BBDF)',
          }
        }
        , typography: {
          fontFamily: locale === 'ar'
            ? '"Noto Sans Arabic", "Roboto", "Helvetica", "Arial", sans-serif'
            : '"Roboto", "Helvetica", "Arial", sans-serif',
          root: {
            color: '#ccc'
          }
          , h3: {
            fontSize: '1.5rem'
            , fontWeight: 'bold'
          }
          , h5: {
            fontSize: '1.2rem'
          }
          , h4: {
            fontSize: '1.4rem'
          }
        }
        , mixins: {
          drawerWidth: 256
          , formDrawerWidth: DESKTOP_FORM_DRAWER_WIDTH_PX
          , toolbar: {
            minHeight: 64
          }
        }
        , components: {
          MuiCardHeader: {
            styleOverrides: {
              root: {
                padding: '0 0 0 10px'
              }
            }
          }
          , MuiPaper: {
            styleOverrides: {
              gap: '12px'
            }
          }
          , MuiDataGrid: {
            styleOverrides: {
              root: {
                color: 'black'
                , backgroundColor: '#ccc'
              }
              , columnHeaderTitle: {
                fontWeight: 'bold'
                , textTransform: 'capitalize'
              }
              , toolbarContainer: {
                backgroundColor: '#aaa'
              }
            }
          }
          , MuiCollapse: {
            styleOverrides: {
              root: ({ theme }) => ({
                marginInlineEnd: '0px'
                , marginInlineStart: '10px'
                , boxShadow: theme.palette.mode === 'dark' ? '-2px 0px 1px #f6d552' : '-2px 0px 1px rgba(0, 0, 0, 0.6)'
                , borderRadius: '2px'
              })
            }
          }
        }
      }),
    [mode, locale]
  )

  const routes = [
    { path: '/home', element: <Home themeMode={mode} setThemeMode={setMode} /> },
    { path: '/statistics', element: <Statistics /> },
    { path: '/admin', element: <Admin /> },
    {
      path: '/tools',
      element: (
        <MergeContext>
          <DataMergingAndValidation />
        </MergeContext>
      ),
    },
    { path: '/history', element: <History /> },
    { path: '/help', element: <Help /> },
    { path: '/device-provision', element: <DeviceProvision /> },
    { path: '/package-imports', element: <PackageImportsList /> },
  ]

  const [handledCrash, setHandledCrash] = useState({})
  const handleRowClick = ({ row, fromLockConfirm }) =>
    setHandledCrash({
      id: row['Crash id'],
      organization: row.organization,
      reporterId: row.merge_date ? undefined : row.user_id,
      fromLockConfirm: !!fromLockConfirm,
    })

  const resetHandledCrash = () => setHandledCrash({})

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SwUpdateModal />
      <LayoutModeProvider>
        <DesktopSidebarProvider>
          <ActionLogsProvider>
            <AppRoutes
              mode={mode}
              setMode={setMode}
              routes={routes}
              handledCrash={handledCrash}
              handleRowClick={handleRowClick}
              resetHandledCrash={resetHandledCrash}
            />
          </ActionLogsProvider>
        </DesktopSidebarProvider>
      </LayoutModeProvider>
    </ThemeProvider>
  )
}
